import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsTicketreservationsV1TicketReservationManagementUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/events-ticket-reservations',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-ticket-reservations',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/events-ticket-reservations',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events/v1/ticket-reservations',
        destPath: '/v1/ticket-reservations',
      },
      {
        srcPath: '/events/v1/bulk/ticket-reservations',
        destPath: '/v1/bulk/ticket-reservations',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_ticket-reservations';

/**
 * Creates a ticket reservation.
 *
 * Reserves specified tickets for a limited time while a customer completes their purchase.
 * The reservation will have `PENDING` status and will automatically expire after the
 * event's configured reservation duration (typically 5-30 minutes) unless confirmed.
 */
export function createTicketReservation(
  payload: object
): RequestOptionsFactory<any> {
  function __createTicketReservation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'ticketReservation.createdDate' },
          { path: 'ticketReservation.updatedDate' },
          { path: 'ticketReservation.expirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v1.ticket_reservation',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketreservations.v1.TicketReservationManagement.CreateTicketReservation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketreservationsV1TicketReservationManagementUrl({
        protoPath: '/v1/ticket-reservations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketReservation.createdDate' },
              { path: 'ticketReservation.updatedDate' },
              { path: 'ticketReservation.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createTicketReservation;
}

/** Retrieves a ticket reservation by ID. */
export function getTicketReservation(
  payload: object
): RequestOptionsFactory<any> {
  function __getTicketReservation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v1.ticket_reservation',
      method: 'GET' as any,
      methodFqn:
        'wix.events.ticketreservations.v1.TicketReservationManagement.GetTicketReservation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketreservationsV1TicketReservationManagementUrl({
        protoPath: '/v1/ticket-reservations/{ticketReservationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketReservation.createdDate' },
              { path: 'ticketReservation.updatedDate' },
              { path: 'ticketReservation.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTicketReservation;
}

/**
 * Deletes a ticket reservation permanently.
 *
 * Completely removes the reservation and releases all reserved tickets back into the inventory to available inventory. This action can't be undone.
 *
 * Call [Cancel Ticket Reservation](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-reservations/cancel-ticket-reservation) if you want to change the reservation status while keeping the record.
 */
export function deleteTicketReservation(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteTicketReservation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v1.ticket_reservation',
      method: 'DELETE' as any,
      methodFqn:
        'wix.events.ticketreservations.v1.TicketReservationManagement.DeleteTicketReservation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketreservationsV1TicketReservationManagementUrl({
        protoPath: '/v1/ticket-reservations/{ticketReservationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketReservation.createdDate' },
              { path: 'ticketReservation.updatedDate' },
              { path: 'ticketReservation.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteTicketReservation;
}

/**
 * Updates tags on multiple ticket reservations.
 *
 * Allows adding or removing tags from multiple reservations in a single operation.
 * Specify reservation IDs and the tags to assign or unassign. If a tag appears
 * in both assign and unassign lists, it will be assigned.
 *
 * Use this for immediate tag updates on a known set of reservations.
 */
export function bulkUpdateTicketReservationTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateTicketReservationTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v1.ticket_reservation',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketreservations.v1.TicketReservationManagement.BulkUpdateTicketReservationTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketreservationsV1TicketReservationManagementUrl({
        protoPath: '/v1/bulk/ticket-reservations/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateTicketReservationTags;
}

/**
 * Updates tags on multiple ticket reservations asynchronously using filters.
 *
 * Allows bulk tag updates on reservations matching specified criteria.
 * The operation runs asynchronously and returns a job ID for tracking progress.
 * An empty filter will update all reservations.
 *
 * If a tag appears in both assign and unassign lists, it will be assigned.
 * Use this for large-scale tag operations based on reservation properties.
 */
export function bulkUpdateTicketReservationTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateTicketReservationTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v1.ticket_reservation',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketreservations.v1.TicketReservationManagement.BulkUpdateTicketReservationTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketreservationsV1TicketReservationManagementUrl({
        protoPath: '/v1/bulk/ticket-reservations/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateTicketReservationTagsByFilter;
}

/**
 * Cancels an existing ticket reservation.
 *
 * Changes the reservation status to `CANCELED_MANUALLY` and releases the reserved
 * tickets back to available inventory. Once cancelled, a reservation cannot be
 * restored. Only `PENDING` and `CONFIRMED` reservations can be cancelled.
 */
export function cancelTicketReservation(
  payload: object
): RequestOptionsFactory<any> {
  function __cancelTicketReservation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v1.ticket_reservation',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketreservations.v1.TicketReservationManagement.CancelTicketReservation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketreservationsV1TicketReservationManagementUrl({
        protoPath: '/v1/ticket-reservations/{ticketReservationId}/cancel',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketReservation.createdDate' },
              { path: 'ticketReservation.updatedDate' },
              { path: 'ticketReservation.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelTicketReservation;
}
